import { Button } from "@/components/atoms";
import { Typography } from "@/components/atoms/Typography";
import {
  CircleAlertFilledIcon,
  CircleCheckFilledIcon,
  DirhamIcon,
  // InboxIcon,
  // MailWithCheckIcon,
} from "@/components/atoms/icons";
import { DashedDivider } from "@/components/molecules/DashedDivider";
import { NoteBanner } from "@/components/molecules/NoteBanner";
import { FullScreenLoader, OkDialog } from "@/components/molecules";
import { UnitReservedDialog } from "@/components/molecules/dialogs/UnitReservedDialog";
import { TextHeader } from "@/components/molecules/header/TextHeader";
import { PropertyListingCard } from "@/modules/home/components/PropertyListingCard";
import { bookingService } from "@/core/api/services/bookingService";
import { ALMETLAA_ACTION, ALMETLAA_SCREEN } from "@/core/constants/dynatraceCatalog";
import { useCatalogDynatraceView } from "@/core/hooks/useCatalogDynatraceView";
import { useReservationActions, useTranslation, useUIColors } from "@/core/hooks";
import { trackCatalogEvent } from "@/core/services/trackDynatraceCatalog";
import { logger } from "@/core/logger";
import { useAppStore } from "@/core/stores/appStore";
import { router, useLocalSearchParams } from "expo-router";
import React, { useState } from "react";
import { Platform, ScrollView, StyleSheet, View } from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";

const STEP_ICON_SIZE = 32;

function toNumber(value: string | undefined): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

/** Like toNumber, but preserves "missing" as null instead of defaulting
 * to 0 — used for display-only fields that should hide rather than show 0. */
function toNumberOrUndefined(value: string | undefined): number | null {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

export function ReserveUnitScreen() {
  useCatalogDynatraceView(
    ALMETLAA_SCREEN.RESERVE,
    ALMETLAA_ACTION.RESERVE.PAGE_VIEW,
    true,
  );
  const colors = useUIColors();
  const { t } = useTranslation();
  const insets = useSafeAreaInsets();
  const {
    propertyId,
    unitLabel,
    bedrooms,
    bathrooms,
    area,
    totalPrice,
    iskanLoan,
    handoverDate,
    imageUri,
  } = useLocalSearchParams<{
    propertyId?: string;
    unitLabel?: string;
    bedrooms?: string;
    bathrooms?: string;
    area?: string;
    totalPrice?: string;
    iskanLoan?: string;
    handoverDate?: string;
    imageUri?: string;
  }>();

  useCatalogDynatraceView(
    ALMETLAA_SCREEN.RESERVE,
    ALMETLAA_ACTION.RESERVE.CONFIRM_DIALOG_VIEW,
    true,
    propertyId ? { unitId: propertyId } : undefined,
  );

  const totalPriceValue = toNumber(totalPrice);
  const iskanLoanValue = toNumber(iskanLoan);
  const hasLoan = iskanLoanValue > 0;
  const remainingBalance = Math.max(0, totalPriceValue - iskanLoanValue);
  const hasShortfall = hasLoan && remainingBalance > 0;
  const isWeb = Platform.OS === 'web';

  const customerId = useAppStore((state) => state.auth.user?.customer_id);
  const { refreshBookingDetailAfterMutation } = useReservationActions(null);

  const [reservedVisible, setReservedVisible] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorDialogVisible, setErrorDialogVisible] = useState(false);
  const [errorDialogMessage, setErrorDialogMessage] = useState("");

  const handleReservedOk = () => {
    setReservedVisible(false);
    router.back();
  };

  const handleProceedToReserve = async () => {
    if (!propertyId || isSubmitting) {
      return;
    }

    trackCatalogEvent({
      screen: ALMETLAA_SCREEN.RESERVE,
      actionName: ALMETLAA_ACTION.RESERVE.CONFIRM_CLICK,
      eventType: 'action',
    });
    setIsSubmitting(true);
    try {
      const response = await bookingService.createBooking(propertyId, customerId);
      logger.info("ReserveUnitScreen:createBooking success", { response });
      trackCatalogEvent({
        screen: ALMETLAA_SCREEN.RESERVE,
        actionName: ALMETLAA_ACTION.RESERVE.SUCCESS,
        eventType: 'action',
      });
      // Success first — then sync from a fresh GET (booking-first + profile
      // overlay), same pattern as cancel; avoids building reservedUnit /
      // pendingUnitReservation by hand from the POST response alone.
      await refreshBookingDetailAfterMutation();
      setReservedVisible(true);
    } catch (error) {
      logger.error("ReserveUnitScreen:createBooking failed", error);
      const apiMessage =
        error instanceof Error && error.message.trim().length > 0
          ? error.message
          : "";
      const alreadyReserved = /already reserved|unit (is )?reserved|not available/i.test(
        apiMessage,
      );
      const reserveErrorEvent = {
        screen: ALMETLAA_SCREEN.RESERVE,
        actionName: alreadyReserved
          ? ALMETLAA_ACTION.RESERVE.ALREADY_RESERVED_ERROR
          : ALMETLAA_ACTION.RESERVE.ERROR,
        eventType: 'error' as const,
      };
      if (apiMessage) {
        trackCatalogEvent({
          ...reserveErrorEvent,
          properties: { p_failedMessage: apiMessage },
        });
      } else {
        trackCatalogEvent(reserveErrorEvent);
      }
      // Status-code-based conflict detection (409) removed — prefer backend message when present.
      const message = apiMessage || t("reserveUnit.submissionFailedError");
      setErrorDialogMessage(message);
      setErrorDialogVisible(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <View
      style={[styles.container, { backgroundColor: colors.backgroundLevel2 }]}
    >
      <TextHeader title={t("propertyDetails.reserveUnitCta")} />
      <ScrollView
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
      >
        <Typography variant="title3" color="primary">
          {t("reserveUnit.confirmTitle")}
        </Typography>
        <Typography variant="body" color="secondary" style={styles.subtitle}>
          {t("reserveUnit.confirmSubtitle")}
        </Typography>

        {/* Unit summary card — same list-layout card used in Home's Exclusive Listings */}
        <PropertyListingCard
          layout="list"
          images={imageUri ? [{ uri: imageUri }] : []}
          unitTitle={unitLabel ?? ""}
          bedrooms={toNumberOrUndefined(bedrooms)}
          bathrooms={toNumberOrUndefined(bathrooms)}
          areaSqFt={toNumberOrUndefined(area)}
          price={toNumberOrUndefined(totalPrice)}
          handoverDate={handoverDate ?? null}
        />

        {/* Price breakdown — hidden entirely when there's no price data */}
        {totalPriceValue > 0 && (
          <View
            style={[
              styles.breakdownCard,
              {
                backgroundColor: colors.backgroundLevel1,
                borderColor: colors.outlineDisabled,
              },
            ]}
          >
            <View style={styles.breakdownRow}>
              <Typography variant="body" color="primary">
                {t("reserveUnit.totalUnitPrice")}
              </Typography>
              <View style={styles.amountRow}>
                <DirhamIcon size={14} color={colors.fontPrimary} />
                <Typography variant="body" color="primary">
                  {totalPriceValue.toLocaleString()}
                </Typography>
              </View>
            </View>
            {hasLoan ? (
              <>
                <DashedDivider color={colors.outlineDisabled} />
                <View style={styles.breakdownRow}>
                  <View style={styles.breakdownLabel}>
                    {isWeb || hasShortfall ? null : (
                      <CircleCheckFilledIcon size={16} color={colors.iskanLoanText} />
                    )}
                    <Typography variant="body" color="primary">
                      {t("reserveUnit.iskanLoan")}
                    </Typography>
                  </View>
                  <View style={styles.amountRow}>
                    <DirhamIcon size={14} color={colors.fontPrimary} />
                    <Typography variant="body" color="primary">
                      {iskanLoanValue.toLocaleString()}
                    </Typography>
                  </View>
                </View>
              </>
            ) : null}
            {hasShortfall ? (
              <>
                <DashedDivider color={colors.outlineDisabled} />
                <View style={styles.breakdownRow}>
                  <View style={styles.breakdownLabel}>
                    <CircleAlertFilledIcon size={14} color={colors.remainingBalanceText} />
                    <Typography variant="body" color={colors.remainingBalanceText}>
                      {t("reserveUnit.remainingBalance")}
                    </Typography>
                  </View>
                  <View style={styles.amountRow}>
                    <DirhamIcon size={14} color={colors.remainingBalanceText} />
                    <Typography variant="body" color={colors.remainingBalanceText}>
                      {remainingBalance.toLocaleString()}
                    </Typography>
                  </View>
                </View>
              </>
            ) : null}
          </View>
        )}

        {hasShortfall ? (
          <NoteBanner
            variant="warning"
            title={t("reserveUnit.importantToNote")}
            description={t("reserveUnit.importantNoteBody")}
          />
        ) : null}

        {/* What happens next — commented out per request; markup kept for a quick revert.
        <View
          style={[
            styles.nextBox,
            {
              // backgroundColor: colors.backgroundLevel1,
              borderColor: colors.outlineDisabled,
            },
          ]}
        >
          <Typography variant="headline" color="primary">
            {t("reserveUnit.whatHappensNext")}
          </Typography>
          <View style={styles.timeline}>
            <View style={styles.stepRow}>
              <View style={styles.stepRail}>
                <View
                  style={[
                    styles.stepIconCircle,
                    {
                      backgroundColor: colors.backgroundLevel1,
                      borderColor: colors.outlineDisabled,
                    },
                  ]}
                >
                  <MailWithCheckIcon size={16} color={colors.fontPrimary} />
                </View>
                <View
                  style={[
                    styles.stepConnector,
                    { backgroundColor: colors.outlineDisabled },
                  ]}
                />
              </View>
              <Typography variant="caption" color="secondary" style={styles.stepText}>
                {t("reserveUnit.stepEmail")}
              </Typography>
            </View>
            <View style={styles.stepRow}>
              <View style={styles.stepRail}>
                <View
                  style={[
                    styles.stepIconCircle,
                    {
                      backgroundColor: colors.backgroundLevel1,
                      borderColor: colors.outlineDisabled,
                    },
                  ]}
                >
                  <InboxIcon size={16} color={colors.fontPrimary} />
                </View>
              </View>
              <Typography variant="caption" color="secondary" style={styles.stepText}>
                {t("reserveUnit.stepDeadline")}
              </Typography>
            </View>
          </View>
        </View>
        */}
      </ScrollView>

      <View
        style={[
          styles.footer,
          {
            paddingBottom: Math.max(insets.bottom, 16),
            borderTopColor: colors.outlineInactive,
            backgroundColor: colors.backgroundLevel1,
          },
        ]}
      >
        <Button
          title={t("reserveUnit.proceedCta")}
          variant="primary"
          fullWidth
          disabled={isSubmitting}
          onPress={() => {
            void handleProceedToReserve();
          }}
        />
      </View>

      <UnitReservedDialog visible={reservedVisible} onOk={handleReservedOk} />
      <OkDialog
        visible={errorDialogVisible}
        message={errorDialogMessage}
        onOk={() => setErrorDialogVisible(false)}
      />
      <FullScreenLoader visible={isSubmitting} />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  scrollContent: {
    padding: 16,
    gap: 16,
  },
  subtitle: {
    lineHeight: 18,
  },
  amountRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  breakdownCard: {
    borderWidth: StyleSheet.hairlineWidth,
    borderRadius: 12,
    marginVertical: 4,
    paddingHorizontal: 20,
    paddingVertical: 16,
    gap: 16,
  },
  breakdownRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    // gap: 16,
  },
  breakdownLabel: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    flexShrink: 1,
    minWidth: 0,
  },
  nextBox: {
    borderWidth: Platform.OS === "android" ? 1 : StyleSheet.hairlineWidth,
    borderRadius: 12,
    padding: 16,
    gap: 12,
  },
  timeline: {
    gap: 0,
  },
  stepRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 12,
  },
  stepRail: {
    width: STEP_ICON_SIZE,
    alignItems: "center",
  },
  stepIconCircle: {
    width: STEP_ICON_SIZE,
    height: STEP_ICON_SIZE,
    borderRadius: STEP_ICON_SIZE / 2,
    borderWidth: StyleSheet.hairlineWidth,
    alignItems: "center",
    justifyContent: "center",
  },
  stepConnector: {
    width: 1,
    flex: 1,
    minHeight: 16,
    marginVertical: 4,
  },
  stepText: {
    flex: 1,
    minWidth: 0,
    paddingTop: 6,
    lineHeight: 16,
    paddingBottom: 12,
  },
  footer: {
    paddingHorizontal: 16,
    paddingTop: 16,
    borderTopWidth: 1,
  },
});

export default ReserveUnitScreen;
